import { AlertTriangleIcon, CheckIcon, HourglassIcon, LoaderIcon, MailIcon, PackageOpenIcon, PlayIcon, XIcon } from 'lucide-react'
import React from 'react'

import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { Badge } from 'slab'
import { AdminComplianceCheckState } from 'utils/web_api.schemas'

interface EmployerComplianceChecksResultBadgeProps {
  state: AdminComplianceCheckState
}

const EmployerComplianceChecksResultBadge: React.FC<EmployerComplianceChecksResultBadgeProps> = ({
  state
}) => {
  const getBadgeType = (state: AdminComplianceCheckState) => {
    switch (state) {
      case AdminComplianceCheckState.initiated:
      case AdminComplianceCheckState.processing:
        return 'caution'
      case AdminComplianceCheckState.errored:
        return 'warning'
      case AdminComplianceCheckState.queued_for_invite:
        return 'info'
      case AdminComplianceCheckState.invite_sent:
      case AdminComplianceCheckState.invite_opened:
        return 'thatch'
      case AdminComplianceCheckState.expired:
        return 'slate'
      case AdminComplianceCheckState.passed:
        return 'success'
      case AdminComplianceCheckState.failed:
        return 'critical'
      default:
        return 'warning'
    }
  }

  const getBadgeIcon = (state: AdminComplianceCheckState) => {
    switch (state) {
      case AdminComplianceCheckState.initiated:
        return PlayIcon
      case AdminComplianceCheckState.processing:
        return LoaderIcon
      case AdminComplianceCheckState.errored:
      case AdminComplianceCheckState.expired:
        return AlertTriangleIcon
      case AdminComplianceCheckState.queued_for_invite:
        return HourglassIcon
      case AdminComplianceCheckState.invite_sent:
        return MailIcon
      case AdminComplianceCheckState.invite_opened:
        return PackageOpenIcon
      case AdminComplianceCheckState.passed:
        return CheckIcon
      case AdminComplianceCheckState.failed:
        return XIcon
      default:
        return AlertTriangleIcon
    }
  }

  const getDisplayState = (state: AdminComplianceCheckState) => {
    return state.replace(/_/g, ' ').charAt(0).toUpperCase() + state.replace(/_/g, ' ').slice(1)
  }

  const getTooltipMessage = (state: AdminComplianceCheckState) => {
    switch (state) {
      case AdminComplianceCheckState.initiated:
        return 'Check has been initiated and will start processing soon.'
      case AdminComplianceCheckState.processing:
        return 'Currently processing and should complete in a few moments.'
      case AdminComplianceCheckState.errored:
        return 'Internal error occurred. Please contact Engineering for support.'
      case AdminComplianceCheckState.queued_for_invite:
        return 'Check is queued and waiting for invite to be triggered by Thatch.'
      case AdminComplianceCheckState.invite_sent:
        return 'Invite has been sent for Entity to complete verification'
      case AdminComplianceCheckState.invite_opened:
        return 'Entity has opened invite'
      case AdminComplianceCheckState.expired:
        return 'Invite link has expired. Please create a new Check.'
      case AdminComplianceCheckState.passed:
        return 'Check has passed verification.'
      case AdminComplianceCheckState.failed:
        return 'Check has failed verification.'
      default:
        return 'Unknown compliance check state'
    }
  }

  return (
    <DisplayTooltipOnHover text={getTooltipMessage(state)} asChild>
      <Badge
        type={getBadgeType(state)}
        leftIcon={getBadgeIcon(state)}
      >
        {getDisplayState(state)}
      </Badge>
    </DisplayTooltipOnHover>
  )
}

export default EmployerComplianceChecksResultBadge
